import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Download,
  FileSpreadsheet,
  Package,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import { downloadSpreadsheetTemplate, parseSpreadsheetFile } from "@/utils/spreadsheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createEmptyItemRow = () => ({
  id: "",
  name: "",
  item_code: "",
  description: "",
  hsn_sac: "",
  unit: "NOS",
  gst_rate: "",
  opening_stock_qty: "",
});

const ITEM_TEMPLATE_ROWS = [
  {
    "Item Name": "HP Laptop",
    "Item Code": "HP-LAP-001",
    Description: "15 inch laptop",
    "HSN/SAC": "84713010",
    Unit: "NOS",
    "GST Rate": 18,
    "Opening Stock": 5,
  },
];

const normalizeText = (value) => String(value || "").trim();

const normalizeUploadedItemRow = (row = {}) => ({
  name: normalizeText(row.name || row.item_name || row["Item Name"] || row["Item name"]),
  item_code: normalizeText(row.item_code || row.itemCode || row["Item Code"] || row.Code),
  description: normalizeText(row.description || row.Description),
  hsn_sac: normalizeText(row.hsn_sac || row.hsn || row.sac || row["HSN/SAC"]),
  unit: normalizeText(row.unit || row.Unit) || "NOS",
  gst_rate: normalizeText(row.gst_rate || row["GST Rate"] || row.gstRate),
  opening_stock_qty: normalizeText(
    row.opening_stock_qty || row.openingQty || row.opening_quantity || row["Opening Stock"] || row["Opening Qty"],
  ),
});

const getProcessedMessage = (label, data) => {
  const processedCount = (data?.created_count || 0) + (data?.updated_count || 0);
  const skippedCount = data?.skipped?.length || 0;
  return `${label} ${processedCount} row(s)${skippedCount ? ` and skipped ${skippedCount} row(s)` : ""}.`;
};

export default function ItemMaster() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([createEmptyItemRow()]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editRow, setEditRow] = useState(createEmptyItemRow());
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async (searchTerm = "") => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/inventory/items`, {
        params: searchTerm ? { search: searchTerm } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load item master");
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const updatePreviewRow = (index, field, value) => {
    setPreviewRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyItemRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) =>
      prev.length === 1 ? [createEmptyItemRow()] : prev.filter((_, rowIndex) => rowIndex !== index),
    );
  };

  const removePreviewRow = (index) => {
    setPreviewRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const saveItemRows = async (entries, onSuccess) => {
    const validEntries = entries
      .map((entry) => ({
        ...entry,
        name: normalizeText(entry.name),
        item_code: normalizeText(entry.item_code),
        description: normalizeText(entry.description),
        hsn_sac: normalizeText(entry.hsn_sac),
        unit: normalizeText(entry.unit) || "NOS",
        gst_rate: normalizeText(entry.gst_rate),
        opening_stock_qty: normalizeText(entry.opening_stock_qty),
      }))
      .filter((entry) => entry.name);

    if (validEntries.length === 0) {
      toast.error("Please add at least one item row with item name");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/inventory/items/bulk`,
        { entries: validEntries },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success(getProcessedMessage("Processed", response.data));
      if (onSuccess) {
        onSuccess();
      }
      loadItems(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save item master rows");
    } finally {
      setSaving(false);
    }
  };

  const handleManualSave = async () => {
    await saveItemRows(rows, () => {
      setRows([createEmptyItemRow()]);
    });
  };

  const handlePreviewSave = async () => {
    await saveItemRows(previewRows, () => {
      setPreviewRows([]);
      setPreviewOpen(false);
    });
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setEditRow({
      id: item.id || "",
      name: item.name || "",
      item_code: item.item_code || "",
      description: item.description || "",
      hsn_sac: item.hsn_sac || "",
      unit: item.unit || "NOS",
      gst_rate: String(item.gst_rate ?? ""),
      opening_stock_qty: String(item.opening_stock_qty ?? ""),
    });
  };

  const closeEditDialog = () => {
    setEditingItem(null);
    setEditRow(createEmptyItemRow());
  };

  const handleEditSave = async () => {
    if (!editingItem?.id) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/inventory/items/${editingItem.id}`,
        {
          ...editRow,
          unit: normalizeText(editRow.unit).toUpperCase() || "NOS",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Item master updated successfully");
      closeEditDialog();
      loadItems(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update item");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteTarget?.id) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/inventory/items/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Item deleted successfully");
      setDeleteTarget(null);
      loadItems(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete item");
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateDownload = () => {
    downloadSpreadsheetTemplate("item-master-format.xlsx", ITEM_TEMPLATE_ROWS, "Item Master");
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      const sheetRows = await parseSpreadsheetFile(file);
      const normalizedRows = sheetRows.map(normalizeUploadedItemRow);

      if (!normalizedRows.some((row) => row.name)) {
        toast.error("The uploaded file does not contain any item rows");
        return;
      }

      setPreviewRows(normalizedRows);
      setPreviewOpen(true);
    } catch (error) {
      toast.error("Failed to read the uploaded Excel file");
    } finally {
      event.target.value = "";
      setImporting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
              Item Master
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Add items manually, save multiple items together, or upload an Excel sheet and review it before saving.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="text-xs" onClick={addRow}>
              <Plus size={14} className="mr-1.5" />
              Add Row
            </Button>
            <Button type="button" variant="outline" className="text-xs" onClick={handleTemplateDownload}>
              <Download size={14} className="mr-1.5" />
              Download Format
            </Button>
            <label className="inline-flex">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={handleFileUpload}
              />
              <span className="inline-flex items-center rounded-md bg-white border border-gray-300 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50">
                <Upload size={14} className="mr-1.5" />
                {importing ? "Reading File..." : "Excel Upload"}
              </span>
            </label>
            <Badge className="bg-gradient-to-r from-primary-500 to-orange-500 text-white">
              <Package size={12} className="mr-1.5" />
              {items.length} Items
            </Badge>
          </div>
        </div>

        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-bold text-gray-800">
              Bulk Create Item Master
            </CardTitle>
            <CardDescription className="text-xs text-gray-500">
              Required Excel columns: `Item Name`, `Item Code`, `Description`, `HSN/SAC`, `Unit`, `GST Rate`.
              Opening stock can be provided using `Opening Stock`.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {rows.map((row, index) => (
              <div key={index} className="grid lg:grid-cols-7 gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">Item Name</Label>
                  <Input
                    value={row.name}
                    onChange={(e) => updateRow(index, "name", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Item Code</Label>
                  <Input
                    value={row.item_code}
                    onChange={(e) => updateRow(index, "item_code", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={row.description}
                    onChange={(e) => updateRow(index, "description", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">HSN/SAC</Label>
                  <Input
                    value={row.hsn_sac}
                    onChange={(e) => updateRow(index, "hsn_sac", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Unit</Label>
                  <Input
                    value={row.unit}
                    onChange={(e) => updateRow(index, "unit", e.target.value.toUpperCase())}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">GST Rate</Label>
                  <Input
                    value={row.gst_rate}
                    onChange={(e) => updateRow(index, "gst_rate", e.target.value)}
                    className="h-9 text-xs"
                    placeholder="18"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Opening Stock</Label>
                  <div className="flex gap-2">
                    <Input
                      value={row.opening_stock_qty}
                      onChange={(e) => updateRow(index, "opening_stock_qty", e.target.value)}
                      className="h-9 text-xs"
                      placeholder="0"
                    />
                    <Button type="button" variant="outline" className="h-9 text-xs" onClick={() => removeRow(index)}>
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleManualSave}
                disabled={saving}
                className="bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white text-xs"
              >
                <Save size={14} className="mr-1.5" />
                {saving ? "Saving..." : "Save All Rows"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-bold text-gray-800">
              Search Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative max-w-md">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    loadItems(search);
                  }
                }}
                placeholder="Search by item name, code or HSN"
                className="pl-9"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-bold text-gray-800">
              Item List
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 flex justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>HSN/SAC</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>GST Rate</TableHead>
                      <TableHead>Opening Stock</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.length > 0 ? (
                      items.map((item) => (
                        <TableRow key={item.id || item.normalized_name}>
                          <TableCell>
                            <div className="font-medium text-sm text-gray-900">
                              {item.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {item.description || "-"}
                            </div>
                          </TableCell>
                          <TableCell>{item.item_code || "-"}</TableCell>
                          <TableCell>{item.hsn_sac || "-"}</TableCell>
                          <TableCell>{item.unit || "-"}</TableCell>
                          <TableCell>{item.gst_rate || "-"}</TableCell>
                          <TableCell className="font-semibold">
                            {Number(item.opening_stock_qty || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                className="h-8 px-2 text-xs"
                                onClick={() => openEditDialog(item)}
                              >
                                <Pencil size={13} className="mr-1" />
                                Edit
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                className="h-8 px-2 text-xs text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50"
                                disabled={Number(item.current_stock_qty || 0) !== 0}
                                onClick={() => setDeleteTarget(item)}
                                title={
                                  Number(item.current_stock_qty || 0) !== 0
                                    ? "Only items with 0 current stock can be deleted"
                                    : "Delete item"
                                }
                              >
                                <Trash2 size={13} className="mr-1" />
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center text-sm text-gray-500 py-10"
                        >
                          No items found yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-6xl">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-gray-900">
                Review Uploaded Item Master
              </DialogTitle>
              <DialogDescription className="text-xs">
                Check the uploaded rows, edit anything you want, then save all rows together.
              </DialogDescription>
            </DialogHeader>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item Name</TableHead>
                    <TableHead>Item Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>HSN/SAC</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>GST Rate</TableHead>
                    <TableHead>Opening Stock</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewRows.length > 0 ? (
                    previewRows.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Input
                            value={row.name}
                            onChange={(e) => updatePreviewRow(index, "name", e.target.value)}
                            className="h-9 text-xs min-w-[180px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.item_code}
                            onChange={(e) => updatePreviewRow(index, "item_code", e.target.value)}
                            className="h-9 text-xs min-w-[140px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.description}
                            onChange={(e) => updatePreviewRow(index, "description", e.target.value)}
                            className="h-9 text-xs min-w-[180px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.hsn_sac}
                            onChange={(e) => updatePreviewRow(index, "hsn_sac", e.target.value)}
                            className="h-9 text-xs min-w-[120px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.unit}
                            onChange={(e) => updatePreviewRow(index, "unit", e.target.value.toUpperCase())}
                            className="h-9 text-xs min-w-[90px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.gst_rate}
                            onChange={(e) => updatePreviewRow(index, "gst_rate", e.target.value)}
                            className="h-9 text-xs min-w-[90px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.opening_stock_qty}
                            onChange={(e) => updatePreviewRow(index, "opening_stock_qty", e.target.value)}
                            className="h-9 text-xs min-w-[110px]"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-9 text-xs"
                            onClick={() => removePreviewRow(index)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-sm text-gray-500 py-8">
                        No uploaded rows to review.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)}>
                Close
              </Button>
              <Button
                type="button"
                onClick={handlePreviewSave}
                disabled={saving || previewRows.length === 0}
                className="bg-gradient-to-r from-primary-500 to-orange-500 text-white"
              >
                <FileSpreadsheet size={14} className="mr-1.5" />
                {saving ? "Saving..." : "Save Uploaded Rows"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(editingItem)} onOpenChange={(open) => !open && closeEditDialog()}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-gray-900">
                Edit Item Master
              </DialogTitle>
              <DialogDescription className="text-xs">
                Update the item details here. Opening stock can also be edited.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Item Name</Label>
                <Input
                  value={editRow.name}
                  onChange={(e) => setEditRow((prev) => ({ ...prev, name: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Item Code</Label>
                <Input
                  value={editRow.item_code}
                  onChange={(e) => setEditRow((prev) => ({ ...prev, item_code: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Input
                  value={editRow.description}
                  onChange={(e) => setEditRow((prev) => ({ ...prev, description: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">HSN/SAC</Label>
                <Input
                  value={editRow.hsn_sac}
                  onChange={(e) => setEditRow((prev) => ({ ...prev, hsn_sac: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Unit</Label>
                <Input
                  value={editRow.unit}
                  onChange={(e) => setEditRow((prev) => ({ ...prev, unit: e.target.value.toUpperCase() }))}
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">GST Rate</Label>
                <Input
                  value={editRow.gst_rate}
                  onChange={(e) => setEditRow((prev) => ({ ...prev, gst_rate: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-xs">Opening Stock</Label>
                <Input
                  value={editRow.opening_stock_qty}
                  onChange={(e) => setEditRow((prev) => ({ ...prev, opening_stock_qty: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeEditDialog}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleEditSave}
                disabled={saving}
                className="bg-gradient-to-r from-primary-500 to-orange-500 text-white"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-gray-900">
                Delete Item
              </DialogTitle>
              <DialogDescription className="text-xs">
                Delete <span className="font-medium">{deleteTarget?.name}</span> from item master. This is allowed only when current stock is 0.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
              Current stock: {Number(deleteTarget?.current_stock_qty || 0).toFixed(2)}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDeleteItem}
                disabled={saving || Number(deleteTarget?.current_stock_qty || 0) !== 0}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {saving ? "Deleting..." : "Delete Item"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
